import React, { useState, useContext, useEffect } from 'react';
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  ThemeProvider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import theme from '../theme';
import { Link, useNavigate } from 'react-router-dom';
import AssessmentIcon from '@mui/icons-material/Assessment';
import FolderIcon from '@mui/icons-material/Folder';
import PolicyIcon from '@mui/icons-material/Policy';
import PeopleIcon from '@mui/icons-material/People';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import authService from '../services/authService';
import Footer from './Footer';

const drawerWidth = 240;

interface Props {
  window?: () => Window;
  children: React.ReactNode;
}

const Layout: React.FC<Props> = (props) => {
  const { window, children } = props;
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const muiTheme = useTheme();
  const [openLogoutDialog, setOpenLogoutDialog] = useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = () => {
    authService.logout();
    navigate('/login');
  };

  const handleOpenLogoutDialog = () => {
    setOpenLogoutDialog(true);
  };

  const handleCloseLogoutDialog = () => {
    setOpenLogoutDialog(false);
  };

  const handleConfirmLogout = () => {
    handleLogout();
    handleCloseLogoutDialog();
  };

  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Toolbar>
        <img src={`${process.env.PUBLIC_URL}/logo_ventana.ico`} alt="Asht Detect Logo" style={{ height: 40, marginRight: 16 }} />
        <Typography variant="h6" noWrap component="div">
          Asht Detect
        </Typography>
      </Toolbar>
      <List>
       <ListItem disablePadding component={Link} to="/clients" onClick={handleDrawerToggle}>
         <ListItemButton>
           <ListItemIcon sx={{ color: 'primary.main' }}>
             <PeopleIcon />
           </ListItemIcon>
           <ListItemText primary="Clientes" sx={{ color: 'text.primary' }} />
         </ListItemButton>
       </ListItem>
        <ListItem disablePadding component={Link} to="/documents" onClick={handleDrawerToggle}>
          <ListItemButton>
            <ListItemIcon sx={{ color: 'primary.main' }}>
              <FolderIcon />
            </ListItemIcon>
            <ListItemText primary="Documentos" sx={{ color: 'text.primary' }} />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding component={Link} to="/audit" onClick={handleDrawerToggle}>
          <ListItemButton>
            <ListItemIcon sx={{ color: 'primary.main' }}>
              <PolicyIcon />
            </ListItemIcon>
            <ListItemText primary="Auditoría" sx={{ color: 'text.primary' }} />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding component={Link} to="/reports" onClick={handleDrawerToggle}>
          <ListItemButton>
            <ListItemIcon sx={{ color: 'primary.main' }}>
              <AssessmentIcon />
            </ListItemIcon>
            <ListItemText primary="Reportes" sx={{ color: 'text.primary' }} />
          </ListItemButton>
        </ListItem>
       <ListItem disablePadding component={Link} to="/dashboard" onClick={handleDrawerToggle}>
         <ListItemButton>
           <ListItemIcon sx={{ color: 'primary.main' }}>
             <DashboardIcon />
           </ListItemIcon>
           <ListItemText primary="Dashboard" sx={{ color: 'text.primary' }} />
         </ListItemButton>
       </ListItem>
      </List>
    </Box>
  );

  const container = window !== undefined ? () => window().document.body : undefined;

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ display: 'flex' }}>
        <CssBaseline />
        <AppBar
          position="fixed"
          sx={{
            zIndex: muiTheme.zIndex.drawer + 1,
            backgroundColor: theme.palette.primary.main,
          }}
        >
          <Toolbar>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
            <img
              src={`${process.env.PUBLIC_URL}/logo_ventana.ico`}
              alt="Asht Detect Logo"
              style={{ height: 40, marginRight: 16 }}
            />
            <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
              Asht Detect
            </Typography>
            <IconButton color="inherit" onClick={handleOpenLogoutDialog}>
              <ExitToAppIcon />
            </IconButton>
          </Toolbar>
        </AppBar>
        <Drawer
          container={container}
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              backgroundColor: theme.palette.background.paper,
            },
          }}
        >
          {drawer}
        </Drawer>
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 3,
            backgroundColor: theme.palette.background.default,
            paddingBottom: '80px', // Add padding to avoid footer overlap
          }}
        >
          <Toolbar />
          {children}
        </Box>
        <Footer />
      </Box>
      <Dialog
        open={openLogoutDialog}
        onClose={handleCloseLogoutDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{"Confirmar Cierre de Sesión"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            ¿Estás seguro de que quieres cerrar sesión?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseLogoutDialog}>No</Button>
          <Button onClick={handleConfirmLogout} autoFocus>
            Sí
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};

export default Layout;